import React, { useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerTopHeader from '../components/ManagerTopHeader';

import { API_BASE } from '../../config/runtime';

const getStoredUser = () => {
  try {
    const rawUser = localStorage.getItem('user');
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
};

const STATUS_COLORS = {
  Pending:   { bg: '#F59E0B', light: '#FEF3C7', text: '#92400E' },
  Approved:  { bg: '#10B981', light: '#D1FAE5', text: '#065F46' },
  Completed: { bg: '#06B6D4', light: '#CFFAFE', text: '#164E63' },
  Declined:  { bg: '#EF4444', light: '#FEE2E2', text: '#991B1B' },
  Cancelled: { bg: '#6B7280', light: '#F3F4F6', text: '#374151' },
};

const normalizeTime = (time) => {
  if (!time) return '09:00:00';
  if (time.length === 5) return `${time}:00`;
  return time;
};

const formatTime = (date) =>
  date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatDate = (date) =>
  date?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

const ManagerCalendar = () => {
  const user = getStoredUser();
  const branchId = user?.branch_id;

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  // Track current view for responsive default
  const [calendarApi, setCalendarApi] = useState(null);

  const buildServiceLabel = (booking) => {
    if (Array.isArray(booking.services) && booking.services.length > 0)
      return booking.services.map((s) => s.service_name).join(', ');
    return booking.service_name || 'Service';
  };

  const getDurationMinutes = (booking) => {
    if (Array.isArray(booking.services) && booking.services.length > 0)
      return booking.services.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0) || 60;
    return Number(booking.duration_minutes) || 60;
  };

  const mapBookingToEvent = (booking) => {
    const serviceLabel = buildServiceLabel(booking);
    const customerLabel = booking.customer_name || 'Guest';
    const therapistLabel = booking.therapist_name || 'Not Assigned';
    const status = booking.status || 'Pending';
    const color = STATUS_COLORS[status]?.bg || '#94A3B8';
    const timeValue = normalizeTime(booking.appointment_time);
    const start = new Date(`${booking.appointment_date}T${timeValue}`);
    const end = new Date(start.getTime() + getDurationMinutes(booking) * 60000);

    return {
      id: String(booking.booking_id),
      title: `${serviceLabel} · ${customerLabel}`,
      start,
      end,
      extendedProps: { status, therapist: therapistLabel, service: serviceLabel, customer: customerLabel, bookingId: booking.booking_id },
      backgroundColor: color,
      borderColor: color,
    };
  };

  const fetchBookings = async () => {
    if (!branchId) { setLoading(false); setLoadError('No branch assigned to your account.'); return; }
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetch(`${API_BASE}/get_branch_bookings.php?branch_id=${branchId}`);
      const result = await response.json();
      if (result.success) setAppointments((result.bookings || []).map(mapBookingToEvent));
      else setLoadError(result.message || 'Failed to load bookings');
    } catch (error) {
      setLoadError(error.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, [branchId]);

  const calendarEvents = useMemo(() => appointments, [appointments]);

  const handleEventClick = (info) => {
    setSelectedEvent({ ...info.event.extendedProps, start: info.event.start, end: info.event.end });
  };

  // Detect mobile for initial view
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const initialView = isMobile ? 'timeGridDay' : 'timeGridWeek';

  const statusMeta = selectedEvent
    ? STATUS_COLORS[selectedEvent.status] || { bg: '#94A3B8', light: '#F1F5F9', text: '#475569' }
    : null;

  return (
    <div className="flex min-h-screen hygea-app-bg">
      <ManagerSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <ManagerTopHeader pageTitle="Appointment Calendar" />

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">

          {/* ── Page title row ── */}
          <div className="mb-5 sm:mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold text-[#b37b65] uppercase tracking-[0.2em] mb-0.5">Schedule</p>
              <h1 className="text-xl sm:text-2xl font-black text-gray-900 leading-tight">Appointment Calendar</h1>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {Object.entries(STATUS_COLORS).map(([label, meta]) => (
                <span key={label} className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.bg }} />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* ── Error ── */}
          {loadError && (
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-rose-700">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <p className="text-sm font-semibold">{loadError}</p>
            </div>
          )}

          {/* ── Calendar Card ── */}
          <div className="bg-white rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 overflow-hidden">

            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
                <svg className="w-8 h-8 animate-spin text-[#b37b65]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <p className="text-sm font-medium">Loading calendar…</p>
              </div>
            ) : (
              <div className="calendar-wrapper p-3 sm:p-5 lg:p-6">
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView={initialView}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay',
                  }}
                  slotMinTime="08:00:00"
                  slotMaxTime="21:00:00"
                  events={calendarEvents}
                  eventClick={handleEventClick}
                  height="auto"
                  nowIndicator={true}
                  allDaySlot={false}
                  eventClassNames="cursor-pointer transition-opacity hover:opacity-90 rounded-lg text-xs font-semibold"
                  dayMaxEvents={3}
                  windowResize={(arg) => {
                    // Switch to day view on resize to mobile
                    if (arg.view.calendar) {
                      const w = window.innerWidth;
                      if (w < 640 && arg.view.type === 'timeGridWeek') {
                        arg.view.calendar.changeView('timeGridDay');
                      }
                    }
                  }}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── Event Detail Modal ── */}
      {selectedEvent && (
        <div
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl border border-[#e7d9ce] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-[#f1e6df] bg-[#fdfaf8]">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#b07b60] mb-0.5">Appointment</p>
                <h3 className="text-base sm:text-lg font-black text-gray-900">Booking Details</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                className="rounded-full border border-[#e8d9cc] bg-white p-2 text-gray-500 hover:bg-[#f7eee8] transition"
                aria-label="Close"
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Modal body */}
            <div className="px-5 sm:px-6 py-5 space-y-4">
              {/* Booking ID + Status */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">Booking ID</p>
                  <p className="text-lg font-black text-gray-900">#{selectedEvent.bookingId}</p>
                </div>
                <span
                  className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide"
                  style={{ backgroundColor: statusMeta.light, color: statusMeta.text }}
                >
                  {selectedEvent.status}
                </span>
              </div>

              {/* Detail grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: 'Client', value: selectedEvent.customer },
                  { label: 'Therapist', value: selectedEvent.therapist },
                  { label: 'Service', value: selectedEvent.service, full: true },
                  { label: 'Date', value: formatDate(selectedEvent.start) },
                  {
                    label: 'Time',
                    value: `${formatTime(selectedEvent.start)} – ${formatTime(selectedEvent.end)}`,
                  },
                ].map(({ label, value, full }) => (
                  <div key={label} className={`bg-gray-50 rounded-xl p-3 ${full ? 'sm:col-span-2' : ''}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
                    <p className="text-sm font-semibold text-gray-800 leading-snug">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal footer */}
            <div className="px-5 sm:px-6 py-4 border-t border-[#f1e6df] bg-[#fdfaf8]">
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                className="w-full sm:w-auto sm:ml-auto sm:block rounded-xl bg-[#1c1310] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#2e1f18] transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FullCalendar style overrides ── */}
      <style>{`
        .calendar-wrapper .fc {
          font-family: inherit;
        }
        .calendar-wrapper .fc-toolbar-title {
          font-size: clamp(0.9rem, 2.5vw, 1.25rem);
          font-weight: 900;
          color: #1c1310;
          letter-spacing: -0.02em;
        }
        .calendar-wrapper .fc-button {
          background: #f9f5f2 !important;
          border: 1px solid #e8ddd7 !important;
          color: #5a4030 !important;
          border-radius: 10px !important;
          font-size: 0.75rem !important;
          font-weight: 700 !important;
          padding: 0.35rem 0.75rem !important;
          box-shadow: none !important;
          transition: background 0.15s !important;
        }
        .calendar-wrapper .fc-button:hover {
          background: #f0e6de !important;
        }
        .calendar-wrapper .fc-button-active,
        .calendar-wrapper .fc-button-primary:not(:disabled).fc-button-active {
          background: #1c1310 !important;
          border-color: #1c1310 !important;
          color: #fff !important;
        }
        .calendar-wrapper .fc-toolbar {
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .calendar-wrapper .fc-toolbar-chunk {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem;
        }
        .calendar-wrapper .fc-col-header-cell-cushion,
        .calendar-wrapper .fc-daygrid-day-number {
          font-weight: 700;
          font-size: 0.75rem;
          color: #6b5045;
          text-decoration: none !important;
        }
        .calendar-wrapper .fc-timegrid-slot-label {
          font-size: 0.7rem;
          font-weight: 600;
          color: #9b8a80;
        }
        .calendar-wrapper .fc-event {
          border-radius: 8px !important;
          border: none !important;
          padding: 2px 5px !important;
          font-size: 0.7rem !important;
          font-weight: 700 !important;
        }
        .calendar-wrapper .fc-daygrid-event {
          border-radius: 6px !important;
        }
        .calendar-wrapper .fc-now-indicator-line {
          border-color: #b37b65 !important;
          border-width: 2px !important;
        }
        .calendar-wrapper .fc-scrollgrid {
          border-radius: 12px;
          overflow: hidden;
          border-color: #f0e8e2 !important;
        }
        .calendar-wrapper td, .calendar-wrapper th {
          border-color: #f5ede8 !important;
        }
        .calendar-wrapper .fc-day-today {
          background: #fdf7f4 !important;
        }
        /* Mobile: hide week/month buttons, force narrower toolbar */
        @media (max-width: 480px) {
          .calendar-wrapper .fc-toolbar {
            gap: 0.4rem;
          }
          .calendar-wrapper .fc-toolbar-title {
            font-size: 0.85rem;
          }
          .calendar-wrapper .fc-button {
            font-size: 0.68rem !important;
            padding: 0.28rem 0.55rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ManagerCalendar;
